import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Scene, SceneElement } from '../../types';
import { AssetField } from './AssetPickerModal';
import { assetUrl } from './scenario-api';
import { uniqueId } from './utils';
import { PedaCategorySelect } from './PedaCategorySelect';
import styles from './scenario-studio.module.css';

interface ElementsEditorProps {
  scenarioId: string;
  scene: Scene;
  selectedElementId?: string | null;
  onSelectedElementIdChange?: (id: string | null) => void;
  onChange: (elements: SceneElement[]) => void;
}

export const ElementsEditor: React.FC<ElementsEditorProps> = ({
  scenarioId,
  scene,
  selectedElementId = null,
  onSelectedElementIdChange,
  onChange,
}) => {
  const elements = scene.elements ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const selectElement = useCallback(
    (id: string | null) => {
      setSelectedId(id);
      onSelectedElementIdChange?.(id);
    },
    [onSelectedElementIdChange]
  );

  useEffect(() => {
    if (selectedElementId) {
      setSelectedId(selectedElementId);
    }
  }, [selectedElementId, scene.id]);

  useEffect(() => {
    if (!selectedId) return;
    const sidebarItem = document.getElementById(`element-editor-item-${selectedId}`);
    sidebarItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const previewItem = document.getElementById(`element-editor-preview-${selectedId}`);
    previewItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId, scene.id]);

  const selected = elements.find((e) => e.id === selectedId);

  const updateElement = (id: string, patch: Partial<SceneElement>) => {
    onChange(elements.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const addElement = (type: SceneElement['type']) => {
    const el: SceneElement = {
      id: uniqueId(type),
      type,
      position: { x: 50, y: 50, width: 10, height: 10 },
    };
    onChange([...elements, el]);
    selectElement(el.id);
  };

  const removeElement = (id: string) => {
    if (!window.confirm(`Supprimer l'élément ${id} ?`)) return;
    onChange(elements.filter((e) => e.id !== id));
    if (selectedId === id) selectElement(null);
  };

  const updatePositionFromPointer = useCallback(
    (elementId: string, clientX: number, clientY: number) => {
      const preview = previewRef.current;
      if (!preview) return;
      const rect = preview.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, Math.round(((clientX - rect.left) / rect.width) * 1000) / 10));
      const y = Math.min(100, Math.max(0, Math.round(((clientY - rect.top) / rect.height) * 1000) / 10));
      onChange(
        elements.map((e) =>
          e.id === elementId ? { ...e, position: { ...e.position, x, y } } : e
        )
      );
    },
    [elements, onChange]
  );

  useEffect(() => {
    if (!draggingId) return;
    const onMove = (e: MouseEvent) => updatePositionFromPointer(draggingId, e.clientX, e.clientY);
    const onUp = () => setDraggingId(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggingId, updatePositionFromPointer]);

  return (
    <div className={styles.elementsEditor}>
      <div className={styles.panelHeader}>
        <h3>Éléments de scène</h3>
        <div className={styles.row}>
          {(['character', 'object', 'hotspot', 'text', 'mailbox'] as const).map((t) => (
            <button key={t} type="button" className={styles.btnGhost} onClick={() => addElement(t)} title={`Ajouter un élément « ${t} »`}>
              + {t}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.elementsLayout}>
        <div
          ref={previewRef}
          className={styles.scenePreview}
          style={{
            backgroundImage: scene.background
              ? `url(${assetUrl(scenarioId, scene.background)})`
              : undefined,
          }}
        >
          {elements.map((element) => {
            const isCharacter = element.type === 'character';
            return (
              <div
                key={element.id}
                id={`element-editor-preview-${element.id}`}
                className={`${styles.previewElement} ${selectedId === element.id ? styles.selected : ''}`}
                style={{
                  left: `${element.position.x}%`,
                  top: isCharacter ? 'auto' : `${element.position.y}%`,
                  bottom: isCharacter ? 0 : undefined,
                  width: element.position.width ? `${element.position.width}%` : 'auto',
                  height: isCharacter ? '75%' : element.position.height ? `${element.position.height}%` : 'auto',
                  transform: isCharacter ? 'translateX(-50%)' : 'translate(-50%, -50%)',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectElement(element.id);
                  setDraggingId(element.id);
                }}
              >
                {element.image && <img src={assetUrl(scenarioId, element.image)} alt="" />}
                <span className={styles.elementLabel}>{element.id}</span>
              </div>
            );
          })}
        </div>

        <div className={styles.elementsSidebar} ref={sidebarRef}>
          {elements.map((e) => (
            <button
              key={e.id}
              id={`element-editor-item-${e.id}`}
              type="button"
              className={`${styles.listItem} ${selectedId === e.id ? styles.active : ''}`}
              onClick={() => selectElement(e.id)}
            >
              {e.id} ({e.type})
            </button>
          ))}

          {selected && (
            <div className={styles.card}>
              <div className={styles.field}>
                <label>ID</label>
                <input className={styles.input} value={selected.id} onChange={(ev) => updateElement(selected.id, { id: ev.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Type</label>
                <select
                  className={styles.select}
                  value={selected.type}
                  onChange={(ev) => updateElement(selected.id, { type: ev.target.value as SceneElement['type'] })}
                >
                  <option value="character">character</option>
                  <option value="object">object</option>
                  <option value="hotspot">hotspot</option>
                  <option value="text">text</option>
                  <option value="mailbox">mailbox</option>
                </select>
              </div>
              <AssetField
                scenarioId={scenarioId}
                label="Image"
                value={selected.image ?? ''}
                subdir={selected.type === 'character' ? 'characters' : 'elements'}
                onChange={(image) => updateElement(selected.id, { image })}
              />
              {['x', 'y', 'width', 'height'].map((key) => (
                <div key={key} className={styles.field}>
                  <label>{key} (%)</label>
                  <input
                    className={styles.input}
                    type="number"
                    value={(selected.position as Record<string, number>)[key] ?? 0}
                    onChange={(ev) =>
                      updateElement(selected.id, {
                        position: {
                          ...selected.position,
                          [key]: parseFloat(ev.target.value) || 0,
                        },
                      })
                    }
                  />
                </div>
              ))}
              {selected.type === 'object' && (
                <>
                <div className={styles.field}>
                  <label>pedaShields</label>
                  <input
                    className={styles.input}
                    type="number"
                    value={selected.pedaShields ?? 0}
                    onChange={(ev) =>
                      updateElement(selected.id, { pedaShields: parseInt(ev.target.value, 10) || undefined })
                    }
                  />
                </div>
                {(selected.pedaShields ?? 0) > 0 && (
                  <PedaCategorySelect
                    value={selected.pedaCategory ?? ''}
                    onChange={(pedaCategory) => updateElement(selected.id, { pedaCategory })}
                    required
                  />
                )}
                </>
              )}
              {selected.type === 'text' && (
                <div className={styles.field}>
                  <label>Texte</label>
                  <textarea className={styles.textarea} value={selected.text ?? ''} onChange={(ev) => updateElement(selected.id, { text: ev.target.value })} />
                </div>
              )}
              <button type="button" className={styles.btnDanger} onClick={() => removeElement(selected.id)}>
                Supprimer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
